/**
 * Evaluación de los canales de adquisición.
 *
 * La regla dura de este bloque: **un canal sin conversión medida no aparece en
 * verde ni con un número inventado.** Va en gris, con la palabra "Sin evaluar" y
 * el icono de sin dato. Es la diferencia entre "este canal no funciona" y "nadie
 * midió si funciona", que son dos decisiones distintas.
 *
 * **Por qué la columna de conversión no imprime el porcentaje.** La conversión de
 * cada canal es una carga manual y su base —ganadas y leads de ese canal— vive en
 * el CRM, no en el panel. Un porcentaje cuya base no está en la pantalla está
 * prohibido por la regla de números derivables, y acá además no era inocuo:
 * pegado a la columna de leads invitaba a multiplicar, y esa multiplicación daba
 * 21 ganadas contra las 5 que el funnel de la misma pantalla reporta para todo el
 * mes. Un lector sacaba una cifra 4x la real leyendo dos columnas vecinas.
 *
 * Así que la columna dice **si la conversión está medida y dónde**, no cuánto
 * da. Los dos números auditables de la fila siguen ahí: el volumen de leads del
 * mes, que sale del desglose del funnel de arriba, y la proveniencia de la carga.
 * El porcentaje vuelve a la tabla el día que la API mande el par
 * `ganadas / leads` por canal, y entonces se publica con su base al lado, igual
 * que la columna de conversión del funnel ("26,8% · 38 de 142 Leads").
 *
 * El semáforo de la columna Escalabilidad es sobre la escalabilidad y nada más:
 * Referidos puede tener la mejor conversión del funnel y estar en rojo porque no
 * escala solo. Eso es exactamente lo que la columna tiene que decir.
 */
import {
  CHANNEL_STATUS_LABELS,
  type Channel,
  type ChannelStatus,
  type FunnelDatapoint,
  type FunnelStage,
  type Health,
  type Prioridad,
  type Role,
} from '@ops/contracts'

import { formatMetricValue, formatPeriodoCorto, plural } from '../../lib/format'
import {
  type Column,
  DataTable,
  Icon,
  MissingCell,
  Panel,
  ProgressBar,
  ProvenanceBadge,
  StatusPill,
} from '../../ui'
import {
  canalEvaluado,
  ESCALABILIDAD_PCT,
  ordenarCanales,
  PRIORIDAD_LABEL,
} from './derivar'

/**
 * Tono del estado de un canal. Verde = está corriendo, ámbar = está detenido y
 * alguien tiene que decidir, gris = todavía no se sabe o ya está fuera de juego.
 */
const TONO_STATUS: Record<ChannelStatus, Health> = {
  activo: 'ok',
  en_prueba: 'unknown',
  pausado: 'warning',
  descartado: 'unknown',
}

/** Semáforo de escalabilidad. La palabra siempre acompaña al color. */
const TONO_ESCALABILIDAD: Record<Prioridad, Health> = {
  alta: 'ok',
  media: 'warning',
  baja: 'critical',
}

export interface CanalesBlockProps {
  canales: readonly Channel[]
  /** Etapas del funnel, para saber cuál es la de entrada. */
  etapas: readonly FunnelStage[]
  /** Datapoints del mes, incluido el desglose por canal. */
  datapoints: readonly FunnelDatapoint[]
  periodo: string
  rol: Role
  now: Date
}

export function CanalesBlock({
  canales,
  etapas,
  datapoints,
  periodo,
  rol,
  now,
}: CanalesBlockProps) {
  const ordenadas = ordenarCanales(canales)
  const entrada = [...etapas].sort((a, b) => a.orden - b.orden)[0] ?? null

  /** Leads del mes atribuidos a un canal. `null` = no hay desglose cargado. */
  const leadsDe = (canalId: string): number | null => {
    if (entrada === null) return null
    const dp = datapoints.find(
      (d) => d.canalId === canalId && d.stageId === entrada.id,
    )
    return dp === undefined ? null : dp.cantidad
  }

  const columnas: ReadonlyArray<Column<Channel>> = [
    {
      key: 'canal',
      header: 'Canal',
      rowHeader: true,
      sortValue: (c) => c.nombre,
      filterValue: (c) => `${c.nombre} ${c.nota ?? ''}`,
      render: (c) => (
        <div className="min-w-0">
          <span className="text-base">{c.nombre}</span>
          {c.nota !== null ? (
            <p className="mt-0.5 max-w-prosa text-micro font-normal leading-snug text-muted-strong">
              {c.nota}
            </p>
          ) : null}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Estado',
      width: '120px',
      sortValue: (c) => CHANNEL_STATUS_LABELS[c.status],
      render: (c) => (
        <StatusPill severidad={TONO_STATUS[c.status]} size="sm">
          {CHANNEL_STATUS_LABELS[c.status]}
        </StatusPill>
      ),
    },
    {
      key: 'leads',
      header: `Leads de ${formatPeriodoCorto(periodo)}`,
      align: 'right',
      width: '110px',
      sortValue: (c) => leadsDe(c.id),
      render: (c) => {
        const leads = leadsDe(c.id)
        // Regla 2: el resto de la app dice "Sin dato" y ofrece la acción; acá
        // decía "— sin desglose", que es el guion pelado que la regla prohíbe.
        // Sin botón de carga porque el desglose por canal lo escribe el funnel,
        // no un endpoint de esta tabla.
        return leads === null ? (
          <MissingCell que="desglose de leads" />
        ) : (
          formatMetricValue(leads, 'count')
        )
      },
    },
    {
      key: 'conversion',
      header: 'Conversión a ganada',
      width: '180px',
      // Se ordena por si está medida o no, que es lo que la celda muestra.
      // Ordenar por un número que no está en pantalla es otra forma del mismo
      // problema: el lector no puede reproducir el orden que está viendo.
      sortValue: (c) => (canalEvaluado(c) ? 1 : 0),
      render: (c) =>
        // Sin medición no hay número ni verde: gris, con la palabra al lado.
        c.conversionPct === null ? (
          <StatusPill severidad="unknown" size="sm" testId="canal-sin-evaluar">
            Sin evaluar
          </StatusPill>
        ) : (
          // Medida, pero su base (ganadas y leads del canal) no está acá. Se
          // dice dónde leerla en vez de imprimir un porcentaje que no se puede
          // recalcular con nada de esta pantalla.
          <span
            data-testid="canal-conversion-en-crm"
            className="inline-flex flex-wrap items-center gap-1.5 text-micro text-muted-strong"
          >
            <Icon name="externo" size={11} />
            Medida en el CRM, con su base
          </span>
        ),
    },
    {
      key: 'escalabilidad',
      header: 'Escalabilidad',
      width: '150px',
      sortValue: (c) => ESCALABILIDAD_PCT[c.escalabilidad],
      render: (c) => (
        <ProgressBar
          pct={ESCALABILIDAD_PCT[c.escalabilidad]}
          label={`Escalabilidad de ${c.nombre}`}
          severidad={TONO_ESCALABILIDAD[c.escalabilidad]}
          etiqueta={PRIORIDAD_LABEL[c.escalabilidad]}
          size="sm"
        />
      ),
    },
    {
      key: 'prioridad',
      header: 'Prioridad',
      width: '90px',
      sortValue: (c) => PRIORIDAD_LABEL[c.prioridad],
      render: (c) => PRIORIDAD_LABEL[c.prioridad],
    },
    // Plata: la columna no existe para quien no puede verla.
    ...(rol === 'admin'
      ? [
          {
            key: 'costo',
            header: 'Costo mensual',
            align: 'right' as const,
            width: '120px',
            sortValue: (c: Channel) => c.costoMensual,
            render: (c: Channel) => formatMetricValue(c.costoMensual, 'usd'),
          },
        ]
      : []),
    {
      key: 'fuente',
      header: 'Fuente',
      width: '210px',
      render: (c) => (
        <ProvenanceBadge
          provenance={c.provenance}
          now={now}
          variant="inline"
          hrefAdmin="/admin"
        />
      ),
    },
  ]

  const evaluados = canales.filter(canalEvaluado).length

  return (
    <Panel
      titulo="Los seis canales, uno al lado del otro"
      subtitulo="El número comparable de la tabla son los leads del mes, que salen del desglose del funnel de arriba. La conversión es carga manual y su base vive en el CRM: la columna dice si está medida, no cuánto da."
      padding="none"
      footer={
        canales.length === 0 ? undefined : (
          <>
            {formatMetricValue(evaluados, 'count')} de{' '}
            {formatMetricValue(canales.length, 'count')}{' '}
            {plural(canales.length, 'canal', 'canales')} con conversión medida.
          </>
        )
      }
    >
      <DataTable
        columns={columnas}
        rows={ordenadas}
        rowKey={(c) => c.id}
        density="compacta"
        emptyMessage="No hay canales cargados todavía"
        caption={
          <>
            Canales de adquisición ordenados por lo que se puede decidir hoy:
            primero los evaluados y activos, después los que faltan evaluar, al
            final los descartados.
          </>
        }
      />
    </Panel>
  )
}
